// ============================================================================
// searchApi.js  —  RoastBrew search data layer
// ----------------------------------------------------------------------------
// Two distinct search surfaces, by design (see the architecture notes):
//
//   • DROPDOWN  = LEXICAL (text matching).  Powered by `searchSuggest()`, which
//     filters an in-memory index of the catalog (titles / descriptions / tags).
//     Runs per keystroke, no network, no AI. Instant.
//
//   • RESULTS PAGE = SEMANTIC + AI answer.  Fires only on Enter via
//     `searchResults()`. Today this returns keyword-grouped rows with
//     `description: null` standing in for the Claude/semantic answer that isn't
//     wired yet. When the embeddings pipeline + Claude land, they fill
//     `description` and/or add groups — the SHAPE below does not change.
//
// SHIPPED-INDEX MODEL:
//   `buildSearchIndex(catalog)` produces a flat, searchable array. Today it is
//   derived from the slim catalog the client already holds, with MOCK
//   description/tags synthesized so every result row is demonstrable. Later, the
//   real searchable index (with TRUE descriptions/tags) ships in the login /
//   catalog payload, or is fetched once — and ONLY this builder changes.
//   `searchSuggest()` / `searchResults()` stay identical because they operate on
//   the index, not the source.
//
// Callers build the index ONCE (e.g. `useMemo(() => buildSearchIndex(catalog),
// [catalog])`) and reuse it for every keystroke.
//
// CONTRACTS (stable across the eventual backend swap):
//   suggest item → { videoId, title, series, courseId, courses[], thumbnailUrl,
//                    previewUrl, locked, matchType, snippet }
//   results      → { query, bestMatch, description /* Claude text | null */,
//                    groups: [ { key, label, items[] } ] }
// ============================================================================

import { isLocked } from './config';

export const SEARCH_GROUPS = [
  { key: 'titles', label: 'Matches based on video titles', matchType: 'title' },
  { key: 'descriptions', label: 'Matches based on video descriptions', matchType: 'description' },
  { key: 'tags', label: 'Matches based on tags', matchType: 'tag' },
];

// ----------------------------------------------------------------------------
// Index construction
// ----------------------------------------------------------------------------

// MOCK ENRICHMENT — the slim catalog carries no descriptions or tags, so we
// synthesize plausible ones purely to demonstrate the description/tag result
// rows. The real index will deliver these fields from the backend; delete these
// two helpers when that happens.

function mockDescription(title, courseName, subjectName) {
  const where = courseName || subjectName || 'the course';
  return `${title}: a ${where} lesson walking through the core ideas with worked examples and practice.`;
}
function mockTags(title, courseName, subjectName) {
  const raw = `${title} ${courseName || ''} ${subjectName || ''}`.toLowerCase();
  const stop = new Set(['the', 'and', 'for', 'with', 'into', 'a', 'an', 'of', 'to', 'in', 'on']);
  const seen = new Set();
  const tags = [];
  for (const tok of raw.split(/[^a-z0-9]+/)) {
    if (tok.length > 2 && !stop.has(tok) && !seen.has(tok)) { seen.add(tok); tags.push(tok); }
  }
  return tags;
}

// ONE BUILD PER CATALOG. The results page and its own search bar ask for the same
// indexes from the same catalog; without this each built its own copy. Results are
// kept per catalog object, so every caller shares one index, a new catalog gets a
// fresh build, and old entries are dropped automatically (WeakMap). Callers treat
// indexes as read-only, so sharing is safe.
function builtOncePer(build) {
  const cache = new WeakMap();
  return (catalog) => {
    if (!catalog || typeof catalog !== 'object') return build(catalog);
    if (!cache.has(catalog)) cache.set(catalog, build(catalog));
    return cache.get(catalog);
  };
}

// Walk the catalog hierarchy (subjects → courses → chapters → sections → topics)
// into a flat array of searchable records, deduped by videoId. When the SAME
// video is reused across courses, we keep ONE record but accumulate every course
// it appears in under `courses` (so the UI can later show "Featured in 3 courses",
// cross-links, etc.). `series`/`courseId` retain the first-seen course for now.
export const buildSearchIndex = builtOncePer(function buildSearchIndexOnce(catalog) {
  const byId = new Map();
  for (const subject of (catalog || [])) {
    for (const course of (subject.courses || [])) {
      for (const chapter of (course.chapters || [])) {
        for (const section of (chapter.sections || [])) {
          for (const topic of (section.topics || [])) {
            if (!topic.videoId) continue;
            const appearance = { courseId: course.id || null, courseName: course.name || '', subject: subject.name || '' };
            const existing = byId.get(topic.videoId);
            if (existing) {
              // Same video, another course — record the extra appearance once.
              if (appearance.courseId && !existing.courses.some((c) => c.courseId === appearance.courseId)) {
                existing.courses.push(appearance);
              }
              continue;
            }
            const title = topic.name || 'Untitled';
            byId.set(topic.videoId, {
              videoId: topic.videoId,
              title,
              series: course.name || subject.name || '',        // primary (first-seen) label
              courseId: course.id || null,                       // primary course
              courses: appearance.courseId ? [appearance] : [],  // EVERY course this video appears in
              thumbnailUrl: topic.thumbnailUrl || null,
              previewUrl: topic.previewUrl || null,
              tierRequired: topic.tierRequired,
              // Real fields from the backend catalog (AI-written description + key
              // topics from the DB). Fall back to the synthesized values only if the
              // API somehow omits them, so search still degrades gracefully.
              description: topic.description || mockDescription(title, course.name, subject.name),
              tags: (topic.keyTopics && topic.keyTopics.length)
                ? topic.keyTopics
                : mockTags(title, course.name, subject.name),
            });
          }
        }
      }
    }
  }
  return Array.from(byId.values());
});

// ----------------------------------------------------------------------------
// Lexical matching (shared by suggest + results)
// ----------------------------------------------------------------------------

function normalize(s) {
  return (s || '').toString().toLowerCase().trim();
}

// Score a record against the query. Lower score = better match. Returns null if
// the record doesn't match at all. matchType reflects WHERE it matched, strongest
// first: title > description > tag.
function scoreRecord(rec, qNorm, tokens) {
  const title = normalize(rec.title);
  const desc = normalize(rec.description);
  const tags = (rec.tags || []).map(normalize);
  const tagText = tags.join(' ');
  const everyIn = (text) => tokens.every((t) => text.includes(t));

  if (everyIn(title)) {
    let score = 2;                                   // title substring
    if (title.startsWith(qNorm)) score = 0;          // title prefix (best)
    else if (title.split(/\s+/).some((w) => w.startsWith(tokens[0]))) score = 1; // word-prefix
    return { score, matchType: 'title', snippet: rec.series || '' };
  }
  if (everyIn(desc)) {
    return { score: 3, matchType: 'description', snippet: rec.description || '' };
  }
  if (tags.some((tag) => tokens.every((t) => tag.includes(t))) || everyIn(tagText)) {
    const hitTag = tags.find((tag) => tokens.some((t) => tag.includes(t)));
    return { score: 4, matchType: 'tag', snippet: hitTag ? `Tagged: ${hitTag}` : 'Tag match' };
  }
  return null;
}

// Returns matched records (with their score/matchType/snippet) sorted best-first.
function rankMatches(query, index) {
  const qNorm = normalize(query);
  if (!qNorm) return [];
  const tokens = qNorm.split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];

  const hits = [];
  for (const rec of (index || [])) {
    const m = scoreRecord(rec, qNorm, tokens);
    if (m) hits.push({ rec, ...m });
  }
  hits.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    const la = a.rec.title.length, lb = b.rec.title.length;
    if (la !== lb) return la - lb;                   // shorter titles first
    return a.rec.title.localeCompare(b.rec.title);   // stable alpha
  });
  return hits;
}

function toItem(hit, currentTier) {
  const r = hit.rec;
  return {
    videoId: r.videoId,
    title: r.title,
    series: r.series,
    courseId: r.courseId,
    courses: r.courses || [],
    thumbnailUrl: r.thumbnailUrl,
    previewUrl: r.previewUrl,
    tierRequired: r.tierRequired,
    locked: isLocked(r.tierRequired, currentTier),
    matchType: hit.matchType,
    snippet: hit.snippet,
  };
}

// ----------------------------------------------------------------------------
// Public search surfaces
// ----------------------------------------------------------------------------

// DROPDOWN — lexical, instant, per keystroke. Returns the top `limit` items.
export function searchSuggest(query, index, { limit = 8, currentTier = 'free' } = {}) {
  return rankMatches(query, index).slice(0, limit).map((h) => toItem(h, currentTier));
}

// Which fields a record matches (independent — a record can match several).
function fieldMatches(rec, tokens) {
  const title = normalize(rec.title);
  const desc = normalize(rec.description);
  const tags = (rec.tags || []).map(normalize);
  const tagText = tags.join(' ');
  const everyIn = (t) => tokens.every((x) => t.includes(x));
  return {
    title: everyIn(title),
    description: everyIn(desc),
    tag: tags.some((tag) => tokens.every((x) => tag.includes(x))) || everyIn(tagText),
  };
}

// Map a record into a result item, framed by the group it's appearing under.
function toItemAs(rec, matchType, tokens, currentTier) {
  let snippet;
  if (matchType === 'description') snippet = rec.description || '';
  else if (matchType === 'tag') {
    const tag = (rec.tags || []).find((t) => tokens.some((x) => normalize(t).includes(x)));
    snippet = tag ? `Tagged: ${tag}` : 'Tag match';
  } else snippet = rec.series || '';
  return {
    videoId: rec.videoId, title: rec.title, series: rec.series, courseId: rec.courseId, courses: rec.courses || [],
    thumbnailUrl: rec.thumbnailUrl, previewUrl: rec.previewUrl, tierRequired: rec.tierRequired, locked: isLocked(rec.tierRequired, currentTier),
    matchType, snippet,
  };
}

// RESULTS PAGE — keyword-grouped rows. Each group lists every result that matches
// THAT field independently, so a lesson can surface under titles, descriptions, and
// tags at once. The top result is featured in the hero AND may also appear in its
// matching rows (same pattern as the library's hero + first row card).
// `description` (the semantic/Claude answer) is null until that pipeline is wired.
export function searchResults(query, index, { perGroup = 12, currentTier = 'free' } = {}) {
  const hits = rankMatches(query, index);                 // ranked, best-first
  const bestMatch = hits.length ? toItem(hits[0], currentTier) : null;
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  const ordered = hits.map((h) => h.rec);                 // matched records in rank order

  const groups = SEARCH_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    items: ordered
      .filter((r) => fieldMatches(r, tokens)[g.matchType])
      .slice(0, perGroup)
      .map((r) => toItemAs(r, g.matchType, tokens, currentTier)),
  }));

  return {
    query: (query || '').toString(),
    total: hits.length,   // every matching topic, not capped by perGroup
    bestMatch,
    description: null,   // ← Claude/semantic answer fills this later (progressive)
    groups,
  };
}

// ----------------------------------------------------------------------------
// Hierarchy search — courses, chapters and sections, matched on their own name.
// Used by the dropdown (and later the results page). Topics keep using the
// functions above, unchanged.
// ----------------------------------------------------------------------------

// Walk the catalog once into { courses, chapters, sections }. Names are
// normalized here, once, so every keystroke only does string checks.
// Lock tier: every entry carries its own node's tierRequired, read straight from
// the hierarchy (/catalog/hierarchy decides the values). Nodes without an id are
// skipped. The same node reached twice is kept once (keyed by kind + id).
export const buildHierarchyIndex = builtOncePer(function buildHierarchyIndexOnce(catalog) {
  const out = { courses: [], chapters: [], sections: [] };
  const seen = new Set();
  const add = (list, kind, node, entry) => {
    if (!node || node.id == null || node.id === '') return;
    const key = kind + ':' + node.id;
    if (seen.has(key)) return;
    seen.add(key);
    list.push(entry);
  };
  // Same link shape as CoursesPage's coursesPath: /courses/{subject}/{course}/…
  const pathOf = (nodes) => '/courses' + nodes.map((n) => '/' + (n && n.slug ? n.slug : '')).join('');
  for (const subject of (catalog || [])) {
    for (const course of (subject.courses || [])) {
      const courseName = course.name || 'Untitled';
      add(out.courses, 'course', course, {
        kind: 'course', id: course.id, name: courseName, nameNorm: normalize(courseName),
        crumb: subject.name || '', path: pathOf([subject, course]), tierRequired: course.tierRequired,
        node: course, courseId: course.id, symbol: subject.symbol,
      });
      for (const chapter of (course.chapters || [])) {
        const chapterName = chapter.name || 'Untitled';
        add(out.chapters, 'chapter', chapter, {
          kind: 'chapter', id: chapter.id, name: chapterName, nameNorm: normalize(chapterName),
          crumb: courseName, path: pathOf([subject, course, chapter]), tierRequired: chapter.tierRequired,
          node: chapter, courseId: course.id, symbol: subject.symbol,
        });
        for (const section of (chapter.sections || [])) {
          const sectionName = section.name || 'Untitled';
          add(out.sections, 'section', section, {
            kind: 'section', id: section.id, name: sectionName, nameNorm: normalize(sectionName),
            crumb: courseName + ' › ' + chapterName, path: pathOf([subject, course, chapter, section]),
            tierRequired: section.tierRequired,
            node: section, courseId: course.id, symbol: subject.symbol,
          });
        }
      }
    }
  }
  return out;
});

// Rank one list of hierarchy entries by name, with the same rule and ordering as
// video titles: every word must appear in the name; prefix, then word-prefix, then
// substring; then shorter names; then alphabetical. Returns the top `limit`.
function rankNames(list, qNorm, tokens, limit, currentTier) {
  const hits = [];
  for (const e of list) {
    const n = e.nameNorm;
    if (!tokens.every((t) => n.includes(t))) continue;
    let score = 2;
    if (n.startsWith(qNorm)) score = 0;
    else if (n.split(/\s+/).some((w) => w.startsWith(tokens[0]))) score = 1;
    hits.push({ e, score });
  }
  hits.sort((a, b) => (a.score - b.score) || (a.e.name.length - b.e.name.length) || a.e.name.localeCompare(b.e.name));
  return hits.slice(0, limit).map(({ e }) => ({
    kind: e.kind, id: e.id, title: e.name, crumb: e.crumb, path: e.path,
    node: e.node, courseId: e.courseId, symbol: e.symbol,
    tierRequired: e.tierRequired, locked: isLocked(e.tierRequired, currentTier),
  }));
}

// DROPDOWN — sections, chapters and courses whose own name matches the query.
export function suggestHierarchy(query, hIndex, { limit = 3, currentTier = 'free' } = {}) {
  const qNorm = normalize(query);
  const tokens = qNorm.split(/\s+/).filter(Boolean);
  if (!qNorm || !tokens.length || !hIndex) return { sections: [], chapters: [], courses: [] };
  return {
    sections: rankNames(hIndex.sections, qNorm, tokens, limit, currentTier),
    chapters: rankNames(hIndex.chapters, qNorm, tokens, limit, currentTier),
    courses: rankNames(hIndex.courses, qNorm, tokens, limit, currentTier),
  };
}
